import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  BookOpen,
  Phone,
  Mail,
  MapPin,
  Calendar,
  GraduationCap,
  Home,
  UserCheck,
  Target,
} from "lucide-react";
import { RiArrowLeftSLine } from "react-icons/ri";
import axios from "axios";
import Loader from "../../../common/Loader";

const ApplicationDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const applicationId = location.state?.applicationId;

  useEffect(() => {
    if (!applicationId) {
      setError("No application ID provided");
      setIsLoading(false);
      return;
    }
    const fetchApplicationDetails = async () => {
      try {
        const config = {
          method: "get",
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_API_URL}/application/get-application-by-id/${applicationId}`,
          headers: {},
        };
        const startTime = Date.now();
        const response = await axios.request(config);
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(1000 - elapsed, 0);
        if (response.data.success) {
          setApplication(response.data.data);
        }
        setTimeout(() => setIsLoading(false), remaining);
      } catch (err) {
        console.error("Error fetching application details:", err);
        setError(err.message);
        setTimeout(() => setIsLoading(false), 1000);
      }
    };
    fetchApplicationDetails();
  }, [applicationId]);

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 dashboardContainer">
        <Loader />
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen p-6 dashboardContainer">
        <div className="text-center py-16">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
            {error || "Application not found"}
          </h3>
          <button
            onClick={() => navigate("/applications")}
            className="mt-4 bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg"
          >
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 dashboardContainer mt-6 mb-4">
      <div className="w-full mx-auto">
        <div
          onClick={() => navigate("/applications")}
          className="flex items-center gap-1 hover:text-opacity-80 transition-all"
        >
          <RiArrowLeftSLine size={40} className="cursor-pointer" />
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 md:p-4">
            <h1 className="text-3xl md:text-4xl font-bold text-primaryTextColor mb-3">
              {application.course.name}
            </h1>
            <span className="inline-block bg-secondaryColor text-white text-sm font-semibold px-4 py-2 rounded-full uppercase">
              {application.course.category}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 p-3 rounded-full">
                <User className="text-blue-600" size={24} />
              </div>
              <h3 className="font-semibold text-primaryTextColor">Applicant</h3>
            </div>
            <p className="text-secondaryTextColor">{application.name}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-100 p-3 rounded-full">
                <Calendar className="text-green-600" size={24} />
              </div>
              <h3 className="font-semibold text-primaryTextColor">
                Date of Birth
              </h3>
            </div>
            <p className="text-secondaryTextColor">
              {new Date(application.dateOfBirth).toLocaleDateString()}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-purple-100 p-3 rounded-full">
                <GraduationCap className="text-purple-600" size={24} />
              </div>
              <h3 className="font-semibold text-primaryTextColor">
                Qualification
              </h3>
            </div>
            <p className="text-secondaryTextColor">
              {application.lastAcademicQualification}
            </p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="text-primaryColor" size={24} />
              <h2 className="text-2xl font-bold text-primaryTextColor">
                Contact
              </h2>
            </div>
            <div className="space-y-2">
              <p className="text-secondaryTextColor text-lg">
                <span className="font-semibold">Mobile:</span>{" "}
                {application.mobileNumber}
              </p>
              <p className="text-secondaryTextColor text-lg">
                <span className="font-semibold">Residential:</span>{" "}
                {application.residentialPhone}
              </p>
              <p className="text-secondaryTextColor text-lg">
                <span className="font-semibold">Email:</span>{" "}
                {application.email}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="text-primaryColor" size={24} />
              <h2 className="text-2xl font-bold text-primaryTextColor">
                Address
              </h2>
            </div>
            <p className="text-secondaryTextColor text-lg">
              {application.fullAddress}
            </p>
          </div>
        </div>

        {/* Parent/Guardian Info */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="text-primaryColor" size={28} />
            <h2 className="text-2xl md:text-3xl font-bold text-primaryTextColor">
              Parent/Guardian Details
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-secondaryTextColor text-lg mb-2">
                <span className="font-semibold">Name:</span>{" "}
                {application.parentGuardianName}
              </p>
              <p className="text-secondaryTextColor text-lg mb-2">
                <span className="font-semibold">Phone:</span>{" "}
                {application.parentPhoneNumber}
              </p>
              <p className="text-secondaryTextColor text-lg mb-2">
                <span className="font-semibold">Email:</span>{" "}
                {application.parentEmail}
              </p>
              <p className="text-secondaryTextColor text-lg">
                <span className="font-semibold">Occupation:</span>{" "}
                {application.parentOccupation}
              </p>
            </div>
            <div>
              <p className="text-secondaryTextColor text-lg mb-2">
                <span className="font-semibold">Address:</span>{" "}
                {application.parentAddress}
              </p>
              <p className="text-secondaryTextColor text-lg">
                <span className="font-semibold">Support:</span>{" "}
                {application.parentSupport ? "Yes" : "No"}
              </p>
            </div>
          </div>
        </div>

        {/* Goal */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <Target className="text-primaryColor" size={28} />
            <h2 className="text-2xl md:text-3xl font-bold text-primaryTextColor">
              Goal for Course
            </h2>
          </div>
          <p className="text-secondaryTextColor text-lg">
            {application.goalForCourse}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetails;
